/**
 * Trend Quality Index
 *
 * Measures the quality/smoothness of a trend.
 * Based on directional efficiency — how much of the total price
 * movement contributes to the net directional move.
 * Higher values = smoother trend; lower values = choppy.
 *
 * Category: Trend | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const trendQualityIndicator: IndicatorDefinition = {
  id: "trend_quality",
  name: "Trend Quality",
  shortName: "TQI",
  category: "trend",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 5, max: 100 },
  ],
  plots: [
    { key: "tqi", label: "TQI", color: "#26A69A", style: "line", lineWidth: 2 },
    { key: "threshold", label: "Threshold", color: "#555555", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    const closes = candles.map(c => c.close);
    const tqiData: { time: number; value: number }[] = [];
    const thresholdData: { time: number; value: number }[] = [];

    for (let i = period; i < closes.length; i++) {
      const netMove = closes[i] - closes[i - period];
      let totalMove = 0;
      for (let j = i - period + 1; j <= i; j++) {
        totalMove += Math.abs(closes[j] - closes[j - 1]);
      }
      // TQI = net directional move / total absolute movement * sign
      const quality = totalMove > 0 ? (netMove / totalMove) * 100 : 0;

      tqiData.push({ time: candles[i].time, value: quality });
      thresholdData.push({ time: candles[i].time, value: 0 });
    }

    return [
      { key: "tqi", data: tqiData },
      { key: "threshold", data: thresholdData },
    ];
  },
};
